import {
  type AgenticLogicActiveRepairRun,
  GetAgenticLogicRuleRequestPathParamsType,
  GetAgenticLogicRuleResponseBodyType,
} from "@intelligems/ig-types";
import { QueryMeta } from "@src/types/react-query";
import { getAuthToken } from "@src/utils";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api";
import { useSwitchOrganizationByAgenticLogicRule } from "../organizations/switch-organization-by-agentic-logic-rule";
import { agenticLogicKeys } from "../queryKeys";

export const getAgenticLogicRule = async (
  pathParams: GetAgenticLogicRuleRequestPathParamsType
) => {
  const resp = await api.get<GetAgenticLogicRuleResponseBodyType>(
    `/v2/agentic-logic/rules/${pathParams.ruleId}`
  );
  return resp.data;
};

/**
 * How long a terminal `gave_up` run is still polled for its prose half.
 *
 * The diagnosis and counter-proposal are written by a best-effort model pass
 * that runs STRICTLY AFTER the terminal transition is durable, in its own
 * transaction (`diagnoseBestEffort`). The detail read that the failing publish
 * job triggers therefore lands on a give-up whose `diagnosis` is still null,
 * and stopping the poll at `finishedAtTs !== null` froze the workspace on the
 * generic cause-only fallback copy until someone reloaded the page — which is
 * how a run that DID produce a diagnosis still showed "Each fix cleared one
 * problem but another check kept failing" and no counter-proposal.
 *
 * Bounded rather than open-ended: the pass can fail (the workflow version is
 * not live, the model errors), and the row then keeps `diagnosis: null`
 * forever. One model call, so a minute is generous.
 */
export const AGENTIC_LOGIC_DIAGNOSIS_POLL_WINDOW_MS = 60_000;

/**
 * The detail poll's interval, as a pure function of the run and the clock.
 *
 * `false` means "stop polling", matching react-query's `refetchInterval`
 * contract.
 */
export const agenticLogicRuleRefetchIntervalMs = (
  run: AgenticLogicActiveRepairRun | null | undefined,
  now: number
): number | false => {
  if (!run) {
    return false;
  }
  // The loop replaces the publish job under the rail between iterations, and
  // the run row is the only pointer to the next one.
  if (run.finishedAtTs === null) {
    return 4_000;
  }
  if (run.status !== "gave_up") {
    return false;
  }
  // A give-up with no store at all has nothing to wait for.
  if (!run.giveUp) {
    return false;
  }
  if (run.giveUp.diagnosis !== null) {
    return false;
  }
  return now - run.finishedAtTs < AGENTIC_LOGIC_DIAGNOSIS_POLL_WINDOW_MS
    ? 4_000
    : false;
};

export const useAgenticLogicRule = (ruleId: string | undefined) => {
  const switchOrgByRule = useSwitchOrganizationByAgenticLogicRule();
  return useQuery<GetAgenticLogicRuleResponseBodyType, Error>({
    queryKey: agenticLogicKeys.rule(ruleId!),
    queryFn: async () => getAgenticLogicRule({ ruleId: ruleId! }),
    enabled: !!ruleId,
    // There is no websocket topic; the loop's events are seconds-to-minutes
    // apart, so a slow poll is proportionate. See
    // `agenticLogicRuleRefetchIntervalMs` for what keeps it running past the
    // run's terminal transition.
    refetchInterval: (query) =>
      agenticLogicRuleRefetchIntervalMs(
        query.state.data?.activeRepairRun,
        Date.now()
      ),
    // A rule link is org-scoped, so a rule that belongs to another of the
    // user's orgs reads as a 404. Mirror the experience deep-link path and
    // move the session into the owning org instead of showing "not found".
    meta: {
      onError: {
        404: () =>
          switchOrgByRule.mutate({
            token: getAuthToken() || "",
            agenticLogicRuleId: ruleId!,
          }),
      },
    } as QueryMeta,
  });
};

/**
 * A rule's recommendation settings, for a read-only display outside the
 * workspace (an experience's saved-upsell panel).
 *
 * Shares the detail query's cache key but not its behaviour: no repair-run
 * poll, and no org switch on a 404 — a settings label must never move the
 * session to another org.
 */
export const useAgenticLogicRuleSettings = (ruleId: string | undefined) =>
  useQuery<
    GetAgenticLogicRuleResponseBodyType,
    Error,
    GetAgenticLogicRuleResponseBodyType["rule"]["settings"]
  >({
    queryKey: agenticLogicKeys.rule(ruleId!),
    queryFn: async () => getAgenticLogicRule({ ruleId: ruleId! }),
    enabled: !!ruleId,
    select: (data) => data.rule.settings,
  });
